/**
 * Filing target picker — shared by the report screen's photo re-file flow and
 * the Dropbox panel's attach flow, so "where does this photo belong?" is one
 * control with one ordering wherever it is asked.
 */

import { useState } from 'react';
import {
  clausesInSection,
  getPromptsForJob,
  GENERAL_RECOMMENDATIONS_PHOTO_PROMPT_KEY,
  UNASSIGNED_PROMPT_KEY,
  type FieldJobInspectionType,
  type FieldReport,
} from '@cer-autofill/shared';

function inspectionKindForJob(jobId: string): FieldJobInspectionType {
  const upper = jobId.toUpperCase();
  if (upper.startsWith('BTY')) return 'battery';
  if (upper.startsWith('PVD')) return 'pv';
  return 'combo';
}

/**
 * Filing targets for a submission, "still needed" first — the shots the QA
 * rules are currently complaining about. Mirrors the inspector PWA's ordering
 * so the two surfaces teach the same mental model.
 */
export function filingTargetsFor(submission: FieldReport): FilingTargetGroup[] {
  const captured = new Set(submission.photos.map((p) => p.promptKey));
  const kind = inspectionKindForJob(submission.jobId);
  const stillNeeded: FilingTarget[] = [];
  const others: FilingTarget[] = [];
  others.push({
    promptKey: GENERAL_RECOMMENDATIONS_PHOTO_PROMPT_KEY,
    label: 'General inspection / improvement photo',
    hint: 'General recommendations',
  });
  for (const prompt of getPromptsForJob(kind)) {
    const target = { promptKey: prompt.promptKey, label: prompt.shot, hint: prompt.subject };
    if (prompt.required && !captured.has(prompt.promptKey)) stillNeeded.push(target);
    else others.push(target);
  }
  const sections = [...new Set(submission.checklist.map((c) => c.section))];
  const clauseTargets: FilingTarget[] = sections
    .flatMap((section) => clausesInSection(section))
    .map((c) => ({
      promptKey: `clause:${c.clauseId}`,
      label: c.clauseId,
      hint: 'Clause evidence',
    }));

  return [
    { title: 'Still needed', targets: stillNeeded },
    { title: 'Other shots', targets: others },
    { title: 'Clause evidence', targets: clauseTargets },
    {
      title: 'Unfiled',
      targets: [
        {
          promptKey: UNASSIGNED_PROMPT_KEY,
          label: 'Not yet filed',
          hint: 'Clear filing so the photo satisfies nothing',
        },
      ],
    },
  ].filter((g) => g.targets.length > 0);
}

export interface FilingTarget {
  promptKey: string;
  label: string;
  hint: string;
}
export interface FilingTargetGroup {
  title: string;
  targets: FilingTarget[];
}

export function FilingPicker({
  targets,
  busy,
  onPick,
  onCancel,
}: {
  targets: FilingTargetGroup[];
  busy: boolean;
  onPick: (promptKey: string) => void;
  onCancel: () => void;
}) {
  const [filter, setFilter] = useState('');
  const needle = filter.trim().toLowerCase();
  const shown = targets
    .map((g) => ({
      ...g,
      targets: needle
        ? g.targets.filter((t) => `${t.label} ${t.hint}`.toLowerCase().includes(needle))
        : g.targets,
    }))
    .filter((g) => g.targets.length > 0);

  return (
    <div className="filing-picker">
      <input
        type="search"
        autoFocus
        value={filter}
        placeholder="Search shots and clauses"
        onChange={(e) => setFilter(e.target.value)}
      />
      {shown.length === 0 && <p className="muted">Nothing matches that.</p>}
      {shown.map((group) => (
        <div key={group.title} className="filing-picker__group">
          <h4>{group.title}</h4>
          <ul>
            {group.targets.map((t) => (
              <li key={t.promptKey}>
                <button
                  type="button"
                  className="filing-picker__target"
                  disabled={busy}
                  onClick={() => onPick(t.promptKey)}
                >
                  <span>{t.label}</span>
                  <span className="filing-picker__hint">{t.hint}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <button type="button" onClick={onCancel} disabled={busy}>
        Cancel
      </button>
    </div>
  );
}
